import { Check, Download, Mail, Send, X } from 'lucide-react';
import { cn } from '@/components/ui/cn';
import { paneelKop } from './paneelKop';
import type {
  FacturenPaneel,
  HudPaneelData,
  KlantPaneel,
  MailConceptPaneel,
  MailPaneel,
  OffertePaneel,
  PdfPaneel,
  PlanningPaneel,
  ProjectPaneel,
} from '../../services/voiceScreenService';

/**
 * Eén futuristisch HUD-paneel in de Chief-omgeving: donkere glasachtige kaart
 * met dunne brand-rand, hoekaccenten en een mono-kopregel. De inhoud verschilt
 * per schermtype (offerte, facturen, project, klant, planning, pdf). Kleuren
 * uitsluitend via de design-tokens.
 *
 * Panelen zijn per spraak stuurbaar (tool `beheer_scherm`): drie formaten
 * (klein = alleen kop + kernregels, normaal, groot ≈ 1,5× breed) en een korte
 * focus-highlight wanneer Chief een paneel aanwijst.
 */

/** Formaat van een HUD-paneel; per spraak te wijzigen via `beheer_scherm`. */
export type PaneelFormaat = 'normaal' | 'groot' | 'klein';

/** Positie-kolom van een HUD-paneel in de omgeving. */
export type PaneelPositie = 'links' | 'midden' | 'rechts';

/** Breedte per formaat; 'groot' ≈ 1,5× de normale kolombreedte. */
const BREEDTE: Record<PaneelFormaat, string> = {
  klein: 'w-[min(17rem,calc(100vw-1.5rem))]',
  normaal: 'w-[min(22rem,calc(100vw-1.5rem))]',
  groot: 'w-[min(33rem,calc(100vw-1.5rem))]',
};

/** PDF-panelen zijn van zichzelf al breder; 'groot' is gemaximeerd op ~48rem. */
const PDF_BREEDTE: Record<PaneelFormaat, string> = {
  klein: 'w-[min(22rem,calc(100vw-1.5rem))]',
  normaal: 'w-[min(36rem,calc(100vw-1.5rem))]',
  groot: 'w-[min(48rem,calc(100vw-1.5rem))]',
};

const euro = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

/** Kernveld-regel: label links (mono, gedimd), waarde rechts. */
function Veld({ label, waarde }: { label: string; waarde: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-xs">
      <span className="shrink-0 font-mono uppercase tracking-wider text-foreground-tertiary">
        {label}
      </span>
      <span className="truncate text-right text-foreground">{waarde}</span>
    </div>
  );
}

function OfferteInhoud({ paneel }: { paneel: OffertePaneel }) {
  return (
    <div className="space-y-2.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xl font-semibold text-foreground">
          {paneel.bedrag !== null ? euro.format(paneel.bedrag) : '—'}
        </span>
        <span className="rounded-md bg-brand/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand">
          {paneel.status}
        </span>
      </div>
      <Veld label="Klant" waarde={paneel.klant} />
      {paneel.adres ? <Veld label="Adres" waarde={paneel.adres} /> : null}
      {paneel.dagenOpen !== null ? (
        <Veld label="Open" waarde={`${paneel.dagenOpen} dagen`} />
      ) : null}
      {paneel.regels.length > 0 ? (
        <div className="border-t border-theme-border/60 pt-2">
          <div className="space-y-1">
            {paneel.regels.map((regel, i) => (
              <div key={i} className="flex items-baseline justify-between gap-3 text-xs">
                <span className="truncate text-foreground-secondary">{regel.omschrijving}</span>
                <span className="shrink-0 font-mono text-foreground">
                  {euro.format(regel.totaal)}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FacturenInhoud({ paneel }: { paneel: FacturenPaneel }) {
  return (
    <div className="space-y-2">
      <div className="space-y-1.5">
        {paneel.facturen.map((factuur, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span
              aria-hidden="true"
              className={cn(
                'h-1.5 w-1.5 shrink-0 rounded-full',
                factuur.openstaand > 0 ? 'bg-warning' : 'bg-success',
              )}
            />
            <span className="truncate font-mono text-foreground">{factuur.nummer}</span>
            <span className="truncate text-foreground-tertiary">{factuur.status}</span>
            <span className="ml-auto shrink-0 font-mono text-foreground">
              {euro.format(factuur.bedrag)}
            </span>
          </div>
        ))}
      </div>
      <div className="flex items-baseline justify-between border-t border-theme-border/60 pt-2 text-xs">
        <span className="font-mono uppercase tracking-wider text-foreground-tertiary">
          Openstaand
        </span>
        <span
          className={cn(
            'font-mono font-semibold',
            paneel.totaalOpenstaand > 0 ? 'text-warning' : 'text-success',
          )}
        >
          {euro.format(paneel.totaalOpenstaand)}
        </span>
      </div>
      {paneel.termijnen.length > 0 ? (
        <div className="border-t border-theme-border/60 pt-2">
          <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-foreground-tertiary">
            Termijnschema
          </div>
          <div className="space-y-1">
            {paneel.termijnen.map((termijn, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span
                  aria-hidden="true"
                  className={cn(
                    'h-1.5 w-1.5 shrink-0 rounded-full',
                    termijn.gefactureerd ? 'bg-success' : 'bg-info',
                  )}
                />
                <span className="truncate text-foreground-secondary">{termijn.naam}</span>
                <span className="ml-auto shrink-0 font-mono text-foreground">
                  {termijn.bedrag !== null ? euro.format(termijn.bedrag) : `${termijn.percentage}%`}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ProjectInhoud({ paneel }: { paneel: ProjectPaneel }) {
  return (
    <div className="space-y-2">
      <p className="truncate text-sm font-medium text-foreground">{paneel.naam}</p>
      <Veld label="Status" waarde={paneel.status} />
      <Veld label="Klant" waarde={paneel.klant} />
      {paneel.adres ? <Veld label="Adres" waarde={paneel.adres} /> : null}
      {paneel.periode ? <Veld label="Periode" waarde={paneel.periode} /> : null}
      {paneel.facturen ? (
        <div className="flex items-baseline justify-between gap-3 border-t border-theme-border/60 pt-2 text-xs">
          <span className="font-mono uppercase tracking-wider text-foreground-tertiary">
            Facturen ({paneel.facturen.aantal})
          </span>
          <span
            className={cn(
              'font-mono font-semibold',
              paneel.facturen.openstaand > 0 ? 'text-warning' : 'text-success',
            )}
          >
            {paneel.facturen.openstaand > 0
              ? `${euro.format(paneel.facturen.openstaand)} open`
              : 'alles betaald'}
          </span>
        </div>
      ) : null}
      {paneel.meerwerk.length > 0 ? (
        <div className="border-t border-theme-border/60 pt-2">
          <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-foreground-tertiary">
            Meerwerk
          </div>
          <div className="space-y-1">
            {paneel.meerwerk.map((mw, i) => (
              <div key={i} className="flex items-baseline gap-2 text-xs">
                <span className="shrink-0 font-mono text-foreground-tertiary">{mw.nummer}</span>
                <span className="truncate text-foreground-secondary">{mw.status}</span>
                <span className="ml-auto shrink-0 font-mono text-foreground">
                  {euro.format(mw.totaal)}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {paneel.planning.length > 0 ? (
        <div className="border-t border-theme-border/60 pt-2">
          <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-foreground-tertiary">
            Planning · 14 dagen
          </div>
          <div className="space-y-1">
            {paneel.planning.map((item, i) => (
              <div key={i} className="flex items-baseline gap-2 text-xs">
                <span className="shrink-0 font-mono text-foreground-secondary">{item.periode}</span>
                <span className="truncate text-foreground-tertiary">{item.ploeg ?? '—'}</span>
                <span className="ml-auto shrink-0 text-foreground-tertiary">{item.status}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PdfInhoud({ paneel, formaat }: { paneel: PdfPaneel; formaat: PaneelFormaat }) {
  return (
    <div
      className={cn(
        'motion-safe:transition-[height] motion-safe:duration-300',
        // 'groot' maakt de PDF ook hoger, niet alleen breder.
        formaat === 'groot' ? 'h-[min(72vh,52rem)]' : 'h-[min(58vh,40rem)]',
      )}
    >
      <object
        data={paneel.url}
        type="application/pdf"
        aria-label={`${paneel.referentie} (PDF)`}
        className="h-full w-full rounded-md border border-theme-border/60 bg-dark-tertiary"
      >
        {/* Fallback voor browsers zonder inline PDF-weergave (bijv. iPad). */}
        <div className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center">
          <p className="text-xs text-foreground-secondary">
            De PDF kan hier niet worden weergegeven.
          </p>
          <a
            href={paneel.url}
            download={paneel.bestandsnaam}
            className="inline-flex items-center gap-1.5 rounded-lg border border-brand/40 px-3 py-1.5 text-xs font-medium text-brand transition-colors hover:bg-brand hover:text-white"
          >
            <Download className="h-3.5 w-3.5" />
            Download {paneel.bestandsnaam}
          </a>
        </div>
      </object>
    </div>
  );
}

function MailInhoud({ paneel }: { paneel: MailPaneel }) {
  return (
    <div className="space-y-2.5">
      {/* Afzenderkop: naam + datum, adres eronder */}
      <div>
        <div className="flex items-baseline justify-between gap-3">
          <span className="truncate text-sm font-medium text-foreground">{paneel.afzender}</span>
          <span className="shrink-0 font-mono text-[10px] text-foreground-tertiary">
            {paneel.datum}
          </span>
        </div>
        <p className="truncate text-xs text-foreground-tertiary">{paneel.adres}</p>
      </div>
      <p className="border-t border-theme-border/60 pt-2 text-xs font-semibold text-foreground">
        {paneel.onderwerp}
      </p>
      {/* Gesaneerde platte tekst; alinea's via de bewaarde regeleindes */}
      <p className="whitespace-pre-wrap text-xs leading-relaxed text-foreground-secondary">
        {paneel.body}
      </p>
    </div>
  );
}

function MailConceptInhoud({
  paneel,
  onVerzend,
  verzendBezig,
}: {
  paneel: MailConceptPaneel;
  onVerzend?: () => void;
  verzendBezig: boolean;
}) {
  const verzonden = paneel.status === 'verzonden';
  return (
    <div className="space-y-2.5">
      {verzonden ? (
        <span className="inline-flex items-center gap-1.5 rounded-md bg-success/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-success">
          <Check className="h-3 w-3" />
          Verzonden
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5 rounded-md bg-brand/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand">
          Concept — wacht op verzenden
        </span>
      )}
      <Veld
        label="Aan"
        waarde={paneel.aanNaam ? `${paneel.aanNaam} <${paneel.aan}>` : paneel.aan}
      />
      <Veld label="Onderwerp" waarde={paneel.onderwerp} />
      <p className="whitespace-pre-wrap border-t border-theme-border/60 pt-2 text-xs leading-relaxed text-foreground">
        {paneel.concept}
      </p>
      {paneel.onderbouwing ? (
        <p className="border-t border-theme-border/60 pt-2 text-[11px] leading-relaxed text-foreground-tertiary">
          <span className="font-mono uppercase tracking-wider">Onderbouwing</span> ·{' '}
          {paneel.onderbouwing}
        </p>
      ) : null}
      {!verzonden && onVerzend ? (
        <button
          type="button"
          onClick={onVerzend}
          disabled={verzendBezig}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-active disabled:opacity-50"
        >
          <Send className="h-3.5 w-3.5" />
          {verzendBezig ? 'Verzenden…' : 'Verzend'}
        </button>
      ) : null}
    </div>
  );
}

function KlantInhoud({ paneel }: { paneel: KlantPaneel }) {
  return (
    <div className="space-y-2">
      <Veld label="Relatie" waarde={paneel.bedrijf ?? paneel.relatie} />
      {paneel.plaats ? <Veld label="Plaats" waarde={paneel.plaats} /> : null}
      {paneel.telefoon ? <Veld label="Telefoon" waarde={paneel.telefoon} /> : null}
      {paneel.email ? <Veld label="E-mail" waarde={paneel.email} /> : null}
      {paneel.projecten.length > 0 ? (
        <div className="space-y-1 border-t border-theme-border/60 pt-2">
          {paneel.projecten.map((project, i) => (
            <div key={i} className="flex items-baseline gap-2 text-xs">
              <span className="shrink-0 font-mono text-foreground-tertiary">{project.nummer}</span>
              <span className="truncate text-foreground-secondary">{project.naam}</span>
              <span className="ml-auto shrink-0 text-foreground-tertiary">{project.status}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function PlanningInhoud({ paneel }: { paneel: PlanningPaneel }) {
  return (
    <div className="space-y-1.5">
      {paneel.regels.map((regel, i) => (
        <div key={i} className="text-xs">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-foreground">{regel.omschrijving}</span>
            <span className="shrink-0 font-mono text-foreground-secondary">{regel.periode}</span>
          </div>
          <div className="flex items-baseline justify-between gap-2 text-foreground-tertiary">
            <span className="truncate">{regel.ploeg ?? '—'}</span>
            <span className="shrink-0">{regel.status}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Compacte weergave ('klein'): alleen de kernregels van het schermtype. */
function KleinInhoud({ paneel }: { paneel: HudPaneelData }) {
  switch (paneel.soort) {
    case 'offerte':
      return (
        <>
          <Veld label="Bedrag" waarde={paneel.bedrag !== null ? euro.format(paneel.bedrag) : '—'} />
          <Veld label="Status" waarde={paneel.status} />
        </>
      );
    case 'facturen':
      return (
        <>
          <Veld label="Facturen" waarde={String(paneel.facturen.length)} />
          <Veld label="Openstaand" waarde={euro.format(paneel.totaalOpenstaand)} />
        </>
      );
    case 'project':
      return (
        <>
          <Veld label="Status" waarde={paneel.status} />
          <Veld label="Klant" waarde={paneel.klant} />
        </>
      );
    case 'klant':
      return (
        <>
          <Veld label="Relatie" waarde={paneel.bedrijf ?? paneel.relatie} />
          {paneel.plaats ? <Veld label="Plaats" waarde={paneel.plaats} /> : null}
        </>
      );
    case 'planning':
      return <Veld label="Items" waarde={String(paneel.regels.length)} />;
    case 'pdf':
      return <Veld label="Bestand" waarde={paneel.bestandsnaam} />;
    case 'mail':
      return (
        <>
          <Veld label="Van" waarde={paneel.afzender} />
          <Veld label="Onderwerp" waarde={paneel.onderwerp} />
        </>
      );
    case 'mail_concept':
      return (
        <>
          <Veld label="Aan" waarde={paneel.aanNaam ?? paneel.aan} />
          <Veld label="Status" waarde={paneel.status === 'verzonden' ? 'Verzonden' : 'Concept'} />
        </>
      );
  }
}

interface ChiefHudPaneelProps {
  paneel: HudPaneelData;
  /** Weergaveformaat; per spraak te wijzigen via `beheer_scherm`. */
  formaat: PaneelFormaat;
  /** Korte highlight-ring wanneer Chief dit paneel aanwijst ('focus'). */
  heeftFocus?: boolean;
  onSluit: () => void;
  /** Handmatig vangnet naast de spraakroute: verzend het open mailconcept. */
  onVerzendConcept?: () => void;
  /** Verzenden loopt (voorstel goedkeuren + uitvoeren); knop is dan geblokkeerd. */
  verzendBezig?: boolean;
}

export function ChiefHudPaneel({
  paneel,
  formaat,
  heeftFocus = false,
  onSluit,
  onVerzendConcept,
  verzendBezig = false,
}: ChiefHudPaneelProps) {
  const kop = paneelKop(paneel);
  const isPdf = paneel.soort === 'pdf';
  return (
    <section
      aria-label={`${kop.label}: ${kop.referentie}`}
      className={cn(
        'pointer-events-auto relative overflow-hidden rounded-lg border border-brand/25 bg-dark-secondary/80 shadow-xl backdrop-blur-md motion-safe:animate-fade-in',
        'motion-safe:transition-[width,transform] motion-safe:duration-300',
        (isPdf ? PDF_BREEDTE : BREEDTE)[formaat],
      )}
    >
      {/* Hoekaccenten */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute left-0 top-0 h-3 w-3 border-l-2 border-t-2 border-brand/60"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-0 top-0 h-3 w-3 border-r-2 border-t-2 border-brand/60"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-0 h-3 w-3 border-b-2 border-l-2 border-brand/60"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 right-0 h-3 w-3 border-b-2 border-r-2 border-brand/60"
      />

      {/* Korte highlight-ring bij spraak-focus ("deze bedoel je") */}
      {heeftFocus ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-10 rounded-lg ring-2 ring-inset ring-brand/70 motion-safe:animate-pulse"
        />
      ) : null}

      {/* Mono-kopregel: type + referentie */}
      <header className="flex items-center gap-2 border-b border-theme-border/60 px-3.5 py-2">
        {paneel.soort === 'mail' || paneel.soort === 'mail_concept' ? (
          <Mail aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-brand" />
        ) : null}
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-brand">
          {kop.label}
        </span>
        <span className="truncate font-mono text-[10px] uppercase tracking-wider text-foreground-secondary">
          {kop.referentie}
        </span>
        {paneel.soort === 'pdf' ? (
          <a
            href={paneel.url}
            download={paneel.bestandsnaam}
            className="ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-foreground-tertiary transition-colors hover:bg-dark-tertiary hover:text-foreground"
            aria-label={`${kop.referentie} downloaden`}
            title="PDF downloaden"
          >
            <Download className="h-3.5 w-3.5" />
          </a>
        ) : null}
        <button
          type="button"
          onClick={onSluit}
          className={cn(
            '-mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-foreground-tertiary transition-colors hover:bg-dark-tertiary hover:text-foreground',
            paneel.soort !== 'pdf' && 'ml-auto',
          )}
          aria-label={`Paneel ${kop.label} sluiten`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </header>

      {formaat === 'klein' ? (
        <div className="space-y-1.5 px-3.5 py-2.5">
          <KleinInhoud paneel={paneel} />
        </div>
      ) : paneel.soort === 'pdf' ? (
        <div className="p-2">
          <PdfInhoud paneel={paneel} formaat={formaat} />
        </div>
      ) : (
        <div
          className={cn(
            'overflow-y-auto px-3.5 py-3',
            formaat === 'groot' ? 'max-h-[48vh]' : 'max-h-[30vh]',
          )}
        >
          {paneel.soort === 'offerte' ? <OfferteInhoud paneel={paneel} /> : null}
          {paneel.soort === 'facturen' ? <FacturenInhoud paneel={paneel} /> : null}
          {paneel.soort === 'project' ? <ProjectInhoud paneel={paneel} /> : null}
          {paneel.soort === 'klant' ? <KlantInhoud paneel={paneel} /> : null}
          {paneel.soort === 'planning' ? <PlanningInhoud paneel={paneel} /> : null}
          {paneel.soort === 'mail' ? <MailInhoud paneel={paneel} /> : null}
          {paneel.soort === 'mail_concept' ? (
            <MailConceptInhoud
              paneel={paneel}
              onVerzend={onVerzendConcept}
              verzendBezig={verzendBezig}
            />
          ) : null}
        </div>
      )}
    </section>
  );
}
